import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Users, Store, Package, ShieldAlert, TrendingUp, 
  UserCheck, UserX, Eye, EyeOff, Trash2, Settings as SettingsIcon,
  ClipboardList, FileText, Check, X, ExternalLink, Loader2, LifeBuoy, HardDrive, Sparkles, RefreshCw
} from 'lucide-react';
import AdminSidebar from '../admin/AdminSidebar';
import InfraHealthPanel from '../admin/InfraHealthPanel';
import InfraSettingsPanel from '../admin/InfraSettingsPanel';
import { findItem, visibleModules } from '../admin/modules';
import AdminMembersPanel from '../admin/AdminMembersPanel';
import AuditLogPanel from '../admin/AuditLogPanel';
import FinancePanel from '../admin/FinancePanel';
import KycReviewBox from '../admin/KycReviewBox';
import LegalDocsPanel from '../admin/LegalDocsPanel';
import ComplaintsPanel from '../admin/ComplaintsPanel';
import AdminSupportPanel from '../components/AdminSupportPanel';
import AdminVtonPanel from '../components/AdminVtonPanel';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import { openPrivateFile, refreshPrivateFiles } from '../utils/privateFile';

const DOC_LABELS = {
  dni_anverso: 'DNI/CE Anverso',
  dni_reverso: 'DNI/CE Reverso',
  ficha_ruc: 'Ficha RUC',
  representante_dni_anverso: 'DNI Rep. Anverso',
  representante_dni_reverso: 'DNI Rep. Reverso',
  vigencia_poder: 'Vigencia de Poder',
  comprobante_domicilio: 'Comprobante domicilio',
  foto_fachada: 'Foto fachada',
  foto_interior: 'Foto interior',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, can } = useAuth();
  const [searchParams] = useSearchParams();
  const allowedTab = (id) => visibleModules(can).some((m) => m.items.some((i) => i.id === id));
  const [activeTab, setActiveTab] = useState(() => (allowedTab(searchParams.get('tab')) ? searchParams.get('tab') : visibleModules(can)[0]?.items[0]?.id || 'stats'));
  const [collapsed, setCollapsed] = useState(false);
  const [supportTicketId] = useState(searchParams.get('ticket') || null);
  const [supportBadge, setSupportBadge] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [r2Savings, setR2Savings] = useState(null);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [sellerFilter, setSellerFilter] = useState('pending');
  const [filesVersion, setFilesVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.admin_role) {
      navigate('/login');
      return;
    }
    fetchStats();
    if (can('support.manage')) fetchSupportStats();
  }, [user, authLoading, navigate]);

  const fetchSupportStats = async () => {
    try {
      const res = await api.get('/admin/support/stats');
      setSupportBadge(res.data.needs_attention || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'vendors') {
      fetchVendors();
    } else if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'sellers') {
      fetchSellers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'sellers') fetchSellers();
  }, [sellerFilter]);

  const fetchSellers = async () => {
    try {
      const params = sellerFilter === 'all' ? {} : { status_filter: sellerFilter };
      const response = await api.get('/admin/sellers', { params });
      setSellers(response.data);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar solicitudes de vendedores');
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      await openPrivateFile(`/admin/kyc/documents/${documentId}/file`);
    } catch (error) {
      toast.error('No se pudo abrir el documento');
    }
  };

  const refreshKycFiles = async () => {
    setRefreshing(true);
    refreshPrivateFiles();
    await fetchSellers();
    setFilesVersion((v) => v + 1);
    setRefreshing(false);
    toast.success('Documentos actualizados desde el origen');
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/stats');
      setStats(response.data);
      if (can('storage.read')) {
        try {
          const savings = await api.get('/admin/r2-savings');
          setR2Savings(savings.data);
        } catch (e) { /* non-blocking */ }
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        params: { limit: 100 }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast.error('Error al cargar usuarios');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/admin/vendors', {
        params: { limit: 100 }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Error al cargar vendors:', error);
      toast.error('Error al cargar vendors');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products', {
        params: { limit: 100 }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      toast.error('Error al cargar productos');
    }
  };

  const handleBanUser = async (userId, isBanned) => {
    const reason = window.prompt(`Motivo para ${isBanned ? 'desbloquear' : 'bloquear'} la cuenta (obligatorio, queda en auditoría):`);
    if (!reason || reason.trim().length < 5) {
      if (reason !== null) toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/${isBanned ? 'unban' : 'ban'}`, { reason: reason.trim() });
      toast.success(isBanned ? 'Usuario desbaneado' : 'Usuario baneado');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado del usuario');
    }
  };

  const handleChangeRole = async (userId, currentRole) => {
    const roles = ['usuario', 'vendedor'];
    const newRole = window.prompt(
      `Cambiar rol de usuario\nRol actual: ${currentRole}\n\nRoles disponibles: ${roles.join(', ')}\n\nIngresa el nuevo rol:`
    );
    
    if (!newRole || !roles.includes(newRole.toLowerCase())) {
      return;
    }

    try {
      await api.put(`/admin/users/${userId}/role`, null, {
        params: { new_role: newRole.toLowerCase() }
      });
      toast.success(`Rol cambiado a ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  const handleToggleVendor = async (vendorId, isActive) => {
    try {
      await api.put(`/admin/vendors/${vendorId}/toggle-active`);
      toast.success(isActive ? 'Vendor desactivado' : 'Vendor activado');
      fetchVendors();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cambiar estado del vendor');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('¿Estás seguro? Esto eliminará el vendor y todos sus productos permanentemente.')) {
      return;
    }

    try {
      await api.delete(`/admin/vendors/${vendorId}`);
      toast.success('Vendor eliminado permanentemente');
      fetchVendors();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar vendor');
    }
  };

  const handleToggleProduct = async (productId, isActive) => {
    try {
      await api.put(`/admin/products/${productId}/toggle-active`);
      toast.success(isActive ? 'Producto desactivado' : 'Producto activado');
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cambiar estado del producto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success('Producto eliminado permanentemente');
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar producto');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-48 bg-gray-900 rounded-xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-900 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <AdminSidebar
        active={activeTab}
        onSelect={setActiveTab}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        badges={{ support: supportBadge }}
        user={user}
        onLogout={async () => { await logout(); navigate('/login'); }}
      />

      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-8">
        <div className="mb-8">
          <p className="text-gray-500 text-xs uppercase tracking-wider">{findItem(activeTab)?.module.label}</p>
          <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="admin-section-title">
            {findItem(activeTab)?.item.label}
          </h1>
        </div>

        {activeTab === 'infra' && <InfraHealthPanel />}
        {activeTab === 'infra-settings' && <InfraSettingsPanel />}
        {findItem(activeTab)?.item.pending && (
          <div className="bg-gray-900 border border-dashed border-gray-700 rounded-xl p-8 text-gray-400 max-w-2xl" data-testid="admin-pending-section">
            <p className="text-white font-medium mb-2">Módulo pendiente</p>
            <p className="text-sm">{findItem(activeTab).item.pending}</p>
          </div>
        )}

        {/* Content */}
        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="stats-panel">
            {Object.keys(stats).length === 0 && <p className="text-gray-500 text-sm" data-testid="stats-empty">Tu rol no tiene estadísticas generales asignadas.</p>}
            {stats.total_users !== undefined && <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-blue-500" />
                <span className="text-3xl font-bold text-white">{stats.total_users}</span>
              </div>
              <p className="text-gray-400 text-sm">Total Usuarios</p>
              {stats.banned_users > 0 && (
                <p className="text-red-400 text-xs mt-2">{stats.banned_users} baneados</p>
              )}
            </div>}

            {stats.total_vendors !== undefined && <><div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <Store className="w-8 h-8 text-green-500" />
                <span className="text-3xl font-bold text-white">{stats.total_vendors}</span>
              </div>
              <p className="text-gray-400 text-sm">Total Vendors</p>
              <p className="text-green-400 text-xs mt-2">{stats.active_vendors} activos</p>
            </div>

            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-8 h-8 text-[#FF6B35]" />
                <span className="text-3xl font-bold text-white">{stats.total_products}</span>
              </div>
              <p className="text-gray-400 text-sm">Total Productos</p>
              <p className="text-[#FF6B35] text-xs mt-2">{stats.active_products} activos</p>
            </div></>}
          </div>
        )}

        {activeTab === 'storage' && (
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-testid="r2-savings-panel">
            <div className="flex items-center gap-3 mb-4">
              <HardDrive className="w-6 h-6 text-cyan-400" />
              <h3 className="text-lg font-semibold text-white">Ahorro en almacenamiento R2</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gray-800/60 rounded-lg p-4">
                <p className="text-3xl font-bold text-cyan-400" data-testid="r2-mb-freed">
                  {r2Savings ? r2Savings.total_mb_freed : 0} <span className="text-base text-gray-400">MB</span>
                </p>
                <p className="text-gray-400 text-sm mt-1">Liberados de R2</p>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-4">
                <p className="text-3xl font-bold text-white" data-testid="r2-objects-deleted">
                  {r2Savings ? r2Savings.total_objects_deleted : 0}
                </p>
                <p className="text-gray-400 text-sm mt-1">Objetos eliminados</p>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-4">
                <p className="text-3xl font-bold text-white" data-testid="r2-records-cleaned">
                  {r2Savings ? r2Savings.total_records_cleaned : 0}
                </p>
                <p className="text-gray-400 text-sm mt-1">Registros limpiados</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              {r2Savings && r2Savings.last_run
                ? `Última limpieza: ${new Date(r2Savings.last_run).toLocaleString('es-PE')}`
                : 'Aún no se ha ejecutado el garbage collector.'}
            </p>
          </div>
        )}

        {activeTab === 'vton' && <AdminVtonPanel />}
        {activeTab === 'admins' && <AdminMembersPanel />}
        {activeTab === 'audit' && <AuditLogPanel />}
        {activeTab === 'finance' && <FinancePanel />}
        {activeTab === 'legal-docs' && <LegalDocsPanel />}
        {activeTab === 'complaints' && <ComplaintsPanel />}

        {activeTab === 'support' && (
          <div data-testid="support-admin-content">
            <AdminSupportPanel initialTicketId={supportTicketId} />
          </div>
        )}

        {activeTab === 'sellers' && (
          <div className="space-y-4" data-testid="sellers-panel">
            {/* Filter */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'pending', label: 'Pendientes' },
                { value: 'approved', label: 'Aprobadas' },
                { value: 'rejected', label: 'Rechazadas' },
                { value: 'all', label: 'Todas' },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setSellerFilter(f.value)}
                  data-testid={`seller-filter-${f.value}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    sellerFilter === f.value
                      ? 'bg-[#FF6B35] text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <button onClick={refreshKycFiles} disabled={refreshing} data-testid="kyc-refresh-files"
                title="Los documentos se guardan 30 min en este navegador. Úsalo si el vendedor actualizó algún archivo."
                className="ml-auto flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:text-white transition-colors disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Actualizar documentos
              </button>
            </div>

            {sellers.length === 0 ? (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
                <ClipboardList className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No hay solicitudes en esta categoría</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sellers.map((s) => {
                  const vd = s.vendor_data || {};
                  const statusColor = {
                    pending: 'bg-yellow-500/10 text-yellow-500',
                    approved: 'bg-green-500/10 text-green-500',
                    rejected: 'bg-red-500/10 text-red-500',
                  }[s.vendor_status] || 'bg-gray-500/10 text-gray-400';
                  const statusLabel = { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' }[s.vendor_status];
                  return (
                    <div
                      key={s.user_id}
                      data-testid={`seller-card-${s.user_id}`}
                      className="bg-gray-900 rounded-xl border border-gray-800 p-6"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-white font-semibold text-lg">
                              {s.tienda?.nombre_tienda || 'Tienda sin nombre'}
                            </h3>
                            <span className={`px-2 py-0.5 text-xs rounded ${statusColor}`}>{statusLabel}</span>
                          </div>
                          <p className="text-gray-400 text-sm mt-1">
                            {[s.nombre, s.apellidos].filter(Boolean).join(' ')} · {s.email}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            {vd.tipo_vendedor === 'empresa'
                              ? `Empresa · RUC ${vd.numero_ruc || '—'}${vd.razon_social ? ` · ${vd.razon_social}` : ''}`
                              : 'Persona Natural'}
                            {(vd.categorias && vd.categorias.length > 0) ? ` · ${vd.categorias.length} categoría(s)` : (s.tienda?.categoria ? ` · ${s.tienda.categoria}` : '')}
                          </p>
                        </div>
                      </div>

                      {/* Documents */}
                      <div className="flex flex-wrap gap-2">
                        {(vd.documentos || []).map((d) => (
                          <button
                            key={d.document_id}
                            onClick={() => handleViewDocument(d.document_id)}
                            data-testid={`view-doc-${d.document_id}`}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black border border-gray-800 hover:border-[#FF6B35] text-sm text-gray-300 hover:text-[#FF6B35] transition-colors"
                          >
                            <FileText className="w-4 h-4" />
                            {DOC_LABELS[d.tipo] || d.tipo}
                            <ExternalLink className="w-3 h-3 opacity-60" />
                          </button>
                        ))}
                      </div>

                      {s.vendor_status === 'rejected' && vd.rejection_reason && (
                        <div className="mt-4 text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-2">
                          <strong>Motivo:</strong> {vd.rejection_reason}
                        </div>
                      )}
                      <KycReviewBox key={`${s.user_id}-${filesVersion}`} s={s} reload={fetchSellers} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Rol</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-300">{u.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{u.nombre}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          u.role === 'administrador' ? 'bg-red-500 bg-opacity-10 text-red-500' :
                          u.role === 'vendedor' ? 'bg-blue-500 bg-opacity-10 text-blue-500' :
                          'bg-gray-500 bg-opacity-10 text-gray-400'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          u.is_banned ? 'bg-red-500 bg-opacity-10 text-red-500' : 'bg-green-500 bg-opacity-10 text-green-500'
                        }`}>
                          {u.is_banned ? 'Baneado' : 'Activo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {can('users.change_role') && <Button
                          onClick={() => handleChangeRole(u.id, u.role)}
                          data-testid={`user-role-btn-${u.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <SettingsIcon className="w-4 h-4" />
                        </Button>}
                        <Button
                          onClick={() => handleBanUser(u.id, u.is_banned)}
                          data-testid={`user-ban-btn-${u.id}`}
                          variant="ghost"
                          size="sm"
                          className={u.is_banned ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'}
                        >
                          {u.is_banned ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'vendors' && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tienda</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Categoría</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Entrega</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {vendors.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-300">{v.nombre_tienda}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{v.categoria}</td>
                      <td className="px-6 py-4 text-sm text-gray-300 capitalize">{v.tipo_entrega}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          v.is_active ? 'bg-green-500 bg-opacity-10 text-green-500' : 'bg-gray-500 bg-opacity-10 text-gray-400'
                        }`}>
                          {v.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <Button
                          onClick={() => navigate(`/vendedor/${v.id}`)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {can('vendors.suspend') && <Button
                          onClick={() => handleToggleVendor(v.id, v.is_active)}
                          data-testid={`vendor-toggle-${v.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-yellow-400 hover:text-yellow-300"
                        >
                          {v.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>}
                        {can('vendors.delete') && <Button
                          onClick={() => handleDeleteVendor(v.id)}
                          data-testid={`vendor-delete-${v.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Producto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Categoría</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Precio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {products.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-300">{p.nombre}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{p.categoria}</td>
                      <td className="px-6 py-4 text-sm text-[#FF6B35] font-semibold">
                        S/ {p.precio?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{p.stock}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          p.is_active ? 'bg-green-500 bg-opacity-10 text-green-500' : 'bg-gray-500 bg-opacity-10 text-gray-400'
                        }`}>
                          {p.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <Button
                          onClick={() => navigate(`/producto/${p.id}`)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {can('products.manage') && <><Button
                          onClick={() => handleToggleProduct(p.id, p.is_active)}
                          data-testid={`product-toggle-${p.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-yellow-400 hover:text-yellow-300"
                        >
                          {p.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          onClick={() => handleDeleteProduct(p.id)}
                          data-testid={`product-delete-${p.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button></>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
